import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getWellConfig } from "../../utils/wellsConfig";
import axios from "../../utils/axiosInstance";
import { PRC_WELL } from "../../utils/PRC_WELL";
import { ProgressDialog } from "./progressPopup";
import { toast } from "react-toastify";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export const MainSettingsTab = ({
  mainSettings,
  setLoading,
  selectedTab,
  selectedDevice,
  refreshData,
}) => {
  const [settings, setSettings] = useState(mainSettings);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");

  useEffect(() => {
    setSettings(mainSettings);
  }, [mainSettings]);

  // Handle field updates (hrs, min, sec)
  const handleChange = (index, field, newValue) => {
    setSettings((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, fields: { ...item.fields, [field]: newValue } }
          : item
      )
    );
  };

  const handleSubmit = async () => {
    // Step 1: Check if all fields are filled
    const isAnyFieldEmpty = settings.some((item) => {
      return Object.keys(item.fields).some((field) => {
        return item.fields[field] === "";
      });
    });

    if (isAnyFieldEmpty) {
      console.error("❌ Please fill all fields.");
      toast.error("❌ Please fill all fields before submitting.");
      return;
    }
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }

    // Step 2: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);

    // Step 3: Give React a chance to render before doing heavy work
    setTimeout(() => {
      runDeviceUpdate();
    }, 100); // 100ms is enough to show the dialog
  };

  const runDeviceUpdate = async () => {
    let pollInterval = null;
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForChange = async ({
      unitId,
      registerList,
      lastTime,
      lastResponse,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
            registerList
          );

          const { time: currentTime, response: currentResponse } =
            resp.data.data || {};

          const timeChanged =
            currentTime && Number(currentTime) !== Number(lastTime);

          let valueChanged = false;
          for (const key in currentResponse) {
            if (currentResponse[key] !== lastResponse[key]) {
              valueChanged = true;
              break;
            }
          }

          console.log("⏳ Polling...", {
            currentTime,
            timeChanged,
            valueChanged,
          });

          if (timeChanged && valueChanged) {
            stopPolling();
            onSuccess({
              currentTime,
              currentResponse,
            });
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let registerList = [
        wellConfig.OPEN_TIME?.[0],
        wellConfig.OPEN_TIME?.[1],
        wellConfig.OPEN_TIME?.[2],
        wellConfig.AF_TIME?.[0],
        wellConfig.AF_TIME?.[1],
        wellConfig.AF_TIME?.[2],
        wellConfig.SHUTIN_TIME?.[0],
        wellConfig.SHUTIN_TIME?.[1],
        wellConfig.SHUTIN_TIME?.[2],
        wellConfig.MD_TIME?.[0],
        wellConfig.MD_TIME?.[1],
        wellConfig.MD_TIME?.[2],
        wellConfig.WELL_DEPTH,
        wellConfig.MAX_MISSED_RUN,
        wellConfig.CASING_INJ_SETPOINT,
        wellConfig.CASING_INJ_DEADBAND,
        wellConfig.CASING_INJ_HI_SETPOINT,
        wellConfig.CASING_INJ_HI_DEADBAND,
      ];
      console.log("register list in body", registerList);
      // Step 1: get initial time
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        registerList
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      let lastResponse = initialResp.data.data?.response || {};
      console.log("Initial time:", lastTime);
      console.log("regList:", lastResponse);
      const openTimer = PRC_WELL.getValueFromWell(settings, "Open Timer");
      const openTimerHourValue = openTimer?.fields?.hrs;
      const openTimerMinValue = openTimer?.fields?.min;
      const openTimerSecValue = openTimer?.fields?.sec;

      const afTimer = PRC_WELL.getValueFromWell(settings, "Afterflow Timer");
      const afTimerHourValue = afTimer?.fields?.hrs;
      const afTimerMinValue = afTimer?.fields?.min;
      const afTimerSecValue = afTimer?.fields?.sec;

      const shutinTimer = PRC_WELL.getValueFromWell(settings, "Shutin Timer");
      const shutinTimerHourValue = shutinTimer?.fields?.hrs;
      const shutinTimerMinValue = shutinTimer?.fields?.min;
      const shutinTimerSecValue = shutinTimer?.fields?.sec;

      const mandatoryTimer = PRC_WELL.getValueFromWell(
        settings,
        "Mandatory Shutin Timer"
      );
      const mandatoryTimerHourValue = mandatoryTimer?.fields?.hrs;
      const mandatoryTimerMinValue = mandatoryTimer?.fields?.min;
      const mandatoryTimerSecValue = mandatoryTimer?.fields?.sec;

      const wellDepth = PRC_WELL.getValueFromWell(settings, "Well Depth");
      const wellDepthHourValue = wellDepth?.fields?.value;

      const maxMissedRuns = PRC_WELL.getValueFromWell(
        settings,
        "Max Missed Runs"
      );
      const maxMissedRunsHourValue = maxMissedRuns?.fields?.value;

      const casingInjSetpoint = PRC_WELL.getValueFromWell(
        settings,
        "Casing Inj Split Setpoint"
      );
      const casingInjSetpointHourValue =
        parseFloat(casingInjSetpoint?.fields?.value || 0) * 10;

      const casingInjDeadband = PRC_WELL.getValueFromWell(
        settings,
        "Casing Inj Split Deadband"
      );
      const casingInjDeadbandHourValue =
        parseFloat(casingInjDeadband?.fields?.value || 0) * 10;

      const casingInjHISetpoint = PRC_WELL.getValueFromWell(
        settings,
        "HI Casing Inj Split SP [Inj Split Override]"
      );
      const casingInjHISetpointHourValue =
        parseFloat(casingInjHISetpoint?.fields?.value || 0) * 10;

      const casingInjHIDeadband = PRC_WELL.getValueFromWell(
        settings,
        "HI Casing Inj Split DB [Inj Split Override]"
      );
      const casingInjHIDeadbandHourValue =
        parseFloat(casingInjHIDeadband?.fields?.value || 0) * 10;

      cmd +=
        "," +
        (wellConfig.OPEN_TIME?.[0] - 40001) +
        "," +
        openTimerHourValue +
        "," +
        (wellConfig.OPEN_TIME?.[1] - 40001) +
        "," +
        openTimerMinValue +
        "," +
        (wellConfig.OPEN_TIME?.[2] - 40001) +
        "," +
        openTimerSecValue;
      cmd +=
        "," +
        (wellConfig.AF_TIME?.[0] - 40001) +
        "," +
        afTimerHourValue +
        "," +
        (wellConfig.AF_TIME?.[1] - 40001) +
        "," +
        afTimerMinValue +
        "," +
        (wellConfig.AF_TIME?.[2] - 40001) +
        "," +
        afTimerSecValue;
      cmd +=
        "," +
        (wellConfig.SHUTIN_TIME?.[0] - 40001) +
        "," +
        shutinTimerHourValue +
        "," +
        (wellConfig.SHUTIN_TIME?.[1] - 40001) +
        "," +
        shutinTimerMinValue +
        "," +
        (wellConfig.SHUTIN_TIME?.[2] - 40001) +
        "," +
        shutinTimerSecValue;
      cmd +=
        "," +
        (wellConfig.MD_TIME?.[0] - 40001) +
        "," +
        mandatoryTimerHourValue +
        "," +
        (wellConfig.MD_TIME?.[1] - 40001) +
        "," +
        mandatoryTimerMinValue +
        "," +
        (wellConfig.MD_TIME?.[2] - 40001) +
        "," +
        mandatoryTimerSecValue;
      cmd += "," + (wellConfig.WELL_DEPTH - 40001) + "," + wellDepthHourValue;
      cmd +=
        "," +
        (wellConfig.MAX_MISSED_RUN - 40001) +
        "," +
        maxMissedRunsHourValue;
      cmd +=
        "," +
        (wellConfig.CASING_INJ_SETPOINT - 40001) +
        "," +
        casingInjSetpointHourValue;
      cmd +=
        "," +
        (wellConfig.CASING_INJ_DEADBAND - 40001) +
        "," +
        casingInjDeadbandHourValue;
      cmd +=
        "," +
        (wellConfig.CASING_INJ_HI_SETPOINT - 40001) +
        "," +
        casingInjHISetpointHourValue;
      cmd +=
        "," +
        (wellConfig.CASING_INJ_HI_DEADBAND - 40001) +
        "," +
        casingInjHIDeadbandHourValue;

      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
        { unitId, cmd,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // STEP 3: poll for 30s BEFORE sending P
      pollForChange({
        unitId,
        registerList,
        lastTime,
        lastResponse,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Update done after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === STEP 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            { unitId, cmd: pCmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd}`,},
            { headers: { id: "anything", "Content-Type": "application/json" } }
          );

          console.log("🟢 P command sent, polling again...");

          // === STEP 5: poll AFTER P ===
          pollForChange({
            unitId,
            registerList,
            lastTime,
            lastResponse,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Update done after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again."
              );
            },
          });
        },
      });


    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          bgcolor: "#1e293b",
          px: 1,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ color: "#ffffffff", fontSize: 14 }}>
          Main Settings
        </Typography>
        <Button
          color="primary"
          variant="contained"
          size="small"
          sx={{ fontSize: 12 }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Box>
      <Box
        sx={{
          backgroundColor: "#374151",
          color: "#fff",
          py: 3,
          px: 1,
          width: "100%",
        }}
      >
        {settings.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #a0a0a0ff",
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                flex: 2,
                color: "#ffffffff",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {item.label}
            </Typography>

            <Grid container spacing={2} sx={{ flex: { xs: 2, md: 4 } }}>
              {["hrs", "min", "sec", "value"].map(
                (field) =>
                  item.fields[field] !== undefined && (
                    <Grid
                      key={field}
                      sx={{ display: "flex", alignItems: "center", gap: 2 }}
                    >
                      <TextField
                        label={
                          item.unit !== ""
                            ? item.unit
                            : field === "value"
                            ? ""
                            : field.toUpperCase()
                        }
                        fullWidth
                        size="small"
                        value={item.fields[field]}
                        onChange={(e) =>
                          handleChange(index, field, e.target.value)
                        }
                        InputLabelProps={{
                          style: { color: "#fff", fontSize: 14 },
                        }}
                        InputProps={{
                          sx: {
                            color: "#fff",
                            backgroundColor: "#1f2937",
                            padding: 0.2,
                            fontSize: 12,
                          },
                        }}
                      />
                    </Grid>
                  )
              )}
            </Grid>
          </Box>
        ))}
      </Box>
      <ProgressDialog
        open={progressOpen}
        isSuccess={progressSuccess}
        errorMessage={progressError}
        onClose={() => {
          setProgressOpen(false);
          setProgressError("");
        }}
        onRefresh={async () => {
          setProgressOpen(false);
          setProgressError("");
          await refreshData();
        }}
        loadingMessage="Check Device Response. Operation in-progress ..."
        successMessage="Successfully updated!"
      />
    </Box>
  );
};
